import { Utils } from '@treezor/design-system';

import { ThunkAction, ThunkDispatch } from 'redux-thunk';

import { TemplateService } from '@/services/TemplateService';

import { TAppState } from '@/store/reducers/reducers';
import { ITemplate, ITemplateDesign, ITemplateModel } from '@/store/models/template.model';

import { TAppError } from '@/store/types/base.state';
import { TTemplateActions, TemplateActionTypes } from '@/store/types/template.types';

export const searchTemplates = (): ThunkAction<Promise<void>, TAppState, {}, TTemplateActions> => {
  const request = (): TTemplateActions => ({
    type: TemplateActionTypes.SEARCH_TEMPLATES,
  });
  const fail = (error: TAppError): TTemplateActions => ({
    type: TemplateActionTypes.SEARCH_TEMPLATES_FAIL,
    error,
    documentation: 'https://docs.treezor.com/guide/api-basics/templates.html',
  });
  const success = (templateKeys: string[]): TTemplateActions => ({
    type: TemplateActionTypes.SEARCH_TEMPLATES_SUCCESS,
    shouldSkipToaster: true,
    templateKeys,
  });

  return async (dispatch: ThunkDispatch<TAppState, {}, TTemplateActions>): Promise<void> => {
    dispatch(request());
    try {
      const templateList = await TemplateService.searchTemplates();
      dispatch(success(templateList));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const searchTemplateModel = (
  template: ITemplate,
): ThunkAction<Promise<void>, TAppState, {}, TTemplateActions> => {
  const request = (): TTemplateActions => ({
    type: TemplateActionTypes.SEARCH_TEMPLATE_MODEL,
    group: template.group,
    name: template.name,
  });
  const fail = (error: TAppError): TTemplateActions => ({
    type: TemplateActionTypes.SEARCH_TEMPLATE_MODEL_FAIL,
    group: template.group,
    name: template.name,
    error,
    documentation: 'https://docs.treezor.com/guide/api-basics/templates.html',
  });
  const success = (model: ITemplateModel): TTemplateActions => ({
    type: TemplateActionTypes.SEARCH_TEMPLATE_MODEL_SUCCESS,
    shouldSkipToaster: true,
    group: template.group,
    name: template.name,
    model,
  });

  return async (dispatch: ThunkDispatch<TAppState, {}, TTemplateActions>): Promise<void> => {
    dispatch(request());
    try {
      const model = await TemplateService.searchTemplateModel(template.fullName);

      if (model) {
        const design = await TemplateService.searchTemplateDesign(template.fullName);

        if (design) {
          dispatch(
            success({
              ...model,
              design: Utils.convertbase64ToUTF8(design.template),
            }),
          );
        }
      }
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const saveTemplate = (
  template: ITemplate,
  model: ITemplateModel,
): ThunkAction<Promise<void>, TAppState, {}, TTemplateActions> => {
  const request = (): TTemplateActions => ({
    type: TemplateActionTypes.SAVE_TEMPLATE,
    group: template.group,
    name: template.name,
  });
  const fail = (error: TAppError): TTemplateActions => ({
    type: TemplateActionTypes.SAVE_TEMPLATE_FAIL,
    group: template.group,
    name: template.name,
    error,
    documentation: 'https://docs.treezor.com/guide/api-basics/templates.html',
  });
  const success = (model: ITemplateModel): TTemplateActions => ({
    type: TemplateActionTypes.SAVE_TEMPLATE_SUCCESS,
    group: template.group,
    name: template.name,
    model,
  });

  return async (dispatch: ThunkDispatch<TAppState, {}, TTemplateActions>): Promise<void> => {
    dispatch(request());
    try {
      if (!model.design) return Promise.reject('Design not found');

      const design: ITemplateDesign = { template: Utils.convertUTF8ToBase64(model.design) };
      await TemplateService.saveTemplate(design, model.name);
      dispatch(success(model));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};
